import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Plus, Target, TrendingUp, Award, LogOut, Flame, Trophy, Users, Zap, Crown, ArrowUpRight, ArrowDownRight, Moon, Sun, Gift, Bell, AlertCircle, Clock, CreditCard } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import RatingModal from '../components/RatingModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const h = window.location.hostname;
  return (h === 'localhost' || h.includes('preview.emergentagent.com')) 
    ? (BACKEND_URL || '') 
    : window.location.origin;
};
const API = `${getApiUrl()}/api`;

const Dashboard = ({ user, onLogout, onUpdateUser }) => {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [leaderboard, setLeaderboard] = useState([]);
  const [balances, setBalances] = useState(null);
  const [reminders, setReminders] = useState([]);
  const [theme, setTheme] = useState(user?.theme || 'light');
  const [showRatingModal, setShowRatingModal] = useState(false);

  useEffect(() => {
    fetchData();
    checkShouldRate();
    // Apply theme
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  const checkShouldRate = async () => {
    try {
      const response = await axios.get(`${API}/users/${user.id}/should-rate`);
      if (response.data.should_rate) {
        // Delay showing the modal to give user time to see the dashboard
        setTimeout(() => setShowRatingModal(true), 3000);
      }
    } catch (error) {
      console.error('Failed to check rating status:', error);
    }
  };

  const fetchData = async () => {
    try {
      const [groupsRes, leaderboardRes, userRes, balancesRes, remindersRes] = await Promise.all([
        axios.get(`${API}/groups?user_id=${user.id}`),
        axios.get(`${API}/leaderboard?type=xp`),
        axios.get(`${API}/users/${user.id}`),
        axios.get(`${API}/users/${user.id}/balances`),
        axios.get(`${API}/reminders?user_id=${user.id}`).catch(() => ({ data: [] }))
      ]);
      setGroups(groupsRes.data);
      setLeaderboard(leaderboardRes.data.slice(0, 5));
      setBalances(balancesRes.data);
      setReminders(remindersRes.data || []);
      
      const updatedUser = userRes.data;
      localStorage.setItem('user', JSON.stringify(updatedUser));
      if (onUpdateUser) onUpdateUser(updatedUser);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const toggleTheme = async () => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    
    // Update localStorage immediately
    const updatedUser = { ...user, theme: newTheme };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    if (onUpdateUser) onUpdateUser(updatedUser);
    
    try {
      await axios.put(`${API}/users/${user.id}?theme=${newTheme}`);
    } catch (error) {
      console.error('Failed to save theme preference');
    }
  };

  const getLevelBadge = (level) => {
    const levels = {
      1: { name: 'Starter', class: 'level-1' },
      2: { name: 'Saver', class: 'level-2' },
      3: { name: 'Pro', class: 'level-3' },
      4: { name: 'Elite', class: 'level-4' },
      5: { name: 'Master', class: 'level-5' },
      6: { name: 'Legend', class: 'level-6' }
    };
    return levels[Math.min(level, 6)] || levels[1];
  };

  const markReminderAsDone = async (reminderId) => {
    try {
      await axios.put(`${API}/reminders/${reminderId}/status?status=completed`);
      setReminders(prev => prev.filter(r => r.id !== reminderId));
      toast.success('Reminder marked as done! 🎉');
    } catch (error) {
      toast.error('Failed to update reminder');
    }
  };

  const getPendingReminders = () => {
    return reminders.filter(r => r.status === 'pending' || r.status === 'sent');
  };

  const getBadgeDisplay = (badge) => {
    const badges = {
      week_warrior: { icon: '🔥', name: 'Week Warrior', color: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
      diamond_hand: { icon: '💎', name: 'Diamond Hand', color: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
      star_saver: { icon: '⭐', name: 'Star Saver', color: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300' }
    };
    return badges[badge] || { icon: '🏆', name: badge, color: 'bg-gray-100 text-gray-700' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-violet-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const levelInfo = getLevelBadge(user.level || 1);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-violet-50 dark:from-slate-950 dark:via-slate-900 dark:to-violet-950">
      {/* SEO */}
      <Helmet>
        <title>Dashboard | SharePay - Manage Your Groups & Savings</title>
        <meta name="description" content="View your savings groups, track contributions, check leaderboard rankings, and manage your finances with SharePay dashboard." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      {/* Header */}
      <nav className="bg-card/80 backdrop-blur-xl border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img 
                src="https://customer-assets.emergentagent.com/job_team-expense/artifacts/1f23zen4_Screenshot%202026-01-29%20at%2010.35.14%E2%80%AFPM.png" 
                alt="SharePay" 
                className="w-8 h-8 object-contain"
              />
              <div>
                <h1 className="text-lg font-bold gradient-text" data-testid="dashboard-title">SharePay</h1>
                <p className="text-xs text-muted-foreground">Hey, {user.name}</p>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <Button
                onClick={toggleTheme}
                variant="ghost"
                size="icon"
                className="rounded-full"
                data-testid="theme-toggle"
              >
                {theme === 'light' ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}
              </Button>
              <Button
                onClick={() => navigate('/referral')}
                variant="outline"
                size="sm"
                className="rounded-full hidden sm:flex gradient-primary text-white border-0"
                data-testid="referral-btn"
              >
                <Gift className="w-4 h-4 mr-2" />
                Invite & Earn
              </Button>
              <Button
                onClick={() => navigate('/leaderboard')}
                variant="outline"
                size="sm"
                className="rounded-full hidden sm:flex"
              >
                <Trophy className="w-4 h-4 mr-2" />
                Leaderboard
              </Button>
              <Button
                onClick={() => navigate('/profile')}
                variant="outline"
                size="sm"
                className="rounded-full"
                data-testid="profile-btn"
              >
                Profile
              </Button>
              <Button
                onClick={() => { onLogout(); navigate('/login'); }}
                variant="ghost"
                size="sm"
                className="rounded-full"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Balance Overview - Splitwise Style */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-2xl gradient-primary p-6 text-white">
              <p className="text-sm opacity-90 mb-1">Total Balance</p>
              <p className="text-3xl font-bold">
                {balances?.net_balance >= 0 ? '+' : ''}₹{Math.abs(balances?.net_balance || 0).toLocaleString()}
              </p>
              <p className="text-sm opacity-75 mt-1">
                {balances?.net_balance >= 0 ? 'You are owed overall' : 'You owe overall'}
              </p>
            </div>
            
            <div className="rounded-2xl bg-card border border-border p-6 card-hover">
              <div className="flex items-center gap-2 mb-1">
                <ArrowUpRight className="w-4 h-4 text-green-500" />
                <p className="text-sm text-muted-foreground">You are owed</p>
              </div>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                ₹{(balances?.total_you_are_owed || 0).toLocaleString()}
              </p>
            </div>
            
            <div className="rounded-2xl bg-card border border-border p-6 card-hover">
              <div className="flex items-center gap-2 mb-1">
                <ArrowDownRight className="w-4 h-4 text-red-500" />
                <p className="text-sm text-muted-foreground">You owe</p>
              </div>
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">
                ₹{(balances?.total_you_owe || 0).toLocaleString()}
              </p>
            </div>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-card rounded-2xl p-5 border border-border card-hover"
            data-testid="stats-card-groups"
          >
            <Target className="w-8 h-8 text-violet-600 mb-2" />
            <p className="text-sm text-muted-foreground mb-1">Active Groups</p>
            <p className="text-3xl font-bold">{groups.length}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="bg-gradient-to-br from-orange-50 to-red-50 dark:from-orange-950/30 dark:to-red-950/30 rounded-2xl p-5 border border-orange-200 dark:border-orange-800 card-hover"
            data-testid="stats-card-streak"
          >
            <Flame className="w-8 h-8 text-orange-600 mb-2" />
            <p className="text-sm text-orange-700 dark:text-orange-300 mb-1">Streak</p>
            <p className="text-3xl font-bold text-orange-900 dark:text-orange-100">{user.contribution_streak || 0}</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            onClick={() => navigate('/gamification')}
            className="bg-gradient-to-br from-violet-50 to-purple-50 dark:from-violet-950/30 dark:to-purple-950/30 rounded-2xl p-5 border border-violet-200 dark:border-violet-800 card-hover cursor-pointer hover:border-violet-400 dark:hover:border-violet-600 transition-colors"
            data-testid="stats-card-xp"
          >
            <Zap className="w-8 h-8 text-violet-600 mb-2" />
            <p className="text-sm text-violet-700 dark:text-violet-300 mb-1">XP</p>
            <div className="flex items-center gap-2">
              <p className="text-3xl font-bold text-violet-900 dark:text-violet-100">{user.xp || 0}</p>
              <span className={`level-badge ${levelInfo.class}`}>Lv{user.level || 1}</span>
            </div>
            <p className="text-xs text-violet-600 mt-2">Tap to view rewards →</p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-2xl p-5 border border-green-200 dark:border-green-800 card-hover"
            data-testid="stats-card-trust"
          >
            <TrendingUp className="w-8 h-8 text-green-600 mb-2" />
            <p className="text-sm text-green-700 dark:text-green-300 mb-1">Trust Score</p>
            <p className="text-3xl font-bold text-green-900 dark:text-green-100">{user.trust_score || 100}</p>
          </motion.div>
        </div>

        {/* Badges */}
        {user.badges && user.badges.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="bg-card rounded-2xl p-6 border border-border mb-8"
          >
            <div className="flex items-center gap-2 mb-4">
              <Trophy className="w-5 h-5 text-yellow-600" />
              <h2 className="text-lg font-semibold">Your Badges</h2>
            </div>
            <div className="flex flex-wrap gap-3">
              {user.badges.map((badge, index) => {
                const badgeInfo = getBadgeDisplay(badge);
                return (
                  <div
                    key={index}
                    className={`${badgeInfo.color} rounded-xl px-4 py-2 flex items-center gap-2 font-semibold`}
                  >
                    <span className="text-xl">{badgeInfo.icon}</span>
                    <span className="text-sm">{badgeInfo.name}</span>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Reminders Section - Show only if there are pending reminders */}
          {getPendingReminders().length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="lg:col-span-3 mb-2"
            >
              <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 rounded-2xl p-6 border border-amber-200 dark:border-amber-800">
                <div className="flex items-center gap-2 mb-4">
                  <Bell className="w-5 h-5 text-amber-600" />
                  <h2 className="text-lg font-semibold text-amber-900 dark:text-amber-100">Payment Reminders</h2>
                  <span className="bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full font-medium">
                    {getPendingReminders().length}
                  </span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {getPendingReminders().slice(0, 6).map((reminder) => (
                    <div
                      key={reminder.id}
                      className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-amber-100 dark:border-amber-900/50 shadow-sm"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            reminder.reminder_type === 'contribution' 
                              ? 'bg-violet-100 dark:bg-violet-900/30' 
                              : 'bg-blue-100 dark:bg-blue-900/30'
                          }`}>
                            {reminder.reminder_type === 'contribution' 
                              ? <Target className="w-4 h-4 text-violet-600" />
                              : <CreditCard className="w-4 h-4 text-blue-600" />
                            }
                          </div>
                          <div>
                            <p className="font-semibold text-sm">{reminder.group_name || 'Group Payment'}</p>
                            <p className="text-xs text-muted-foreground capitalize">{reminder.reminder_type}</p>
                          </div>
                        </div>
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          reminder.status === 'sent' 
                            ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' 
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
                        }`}>
                          {reminder.status === 'sent' ? 'Urgent' : 'Due'}
                        </span>
                      </div>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                        <Clock className="w-3 h-3" />
                        <span>Due: {new Date(reminder.due_date).toLocaleDateString()}</span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <p className="text-lg font-bold text-amber-700 dark:text-amber-400">
                          ₹{reminder.amount?.toLocaleString() || '0'}
                        </p>
                        <Button
                          onClick={() => markReminderAsDone(reminder.id)}
                          size="sm"
                          className="rounded-full bg-green-600 hover:bg-green-700 text-white text-xs"
                        >
                          Mark Paid
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
                {getPendingReminders().length > 6 && (
                  <p className="text-center text-sm text-amber-700 dark:text-amber-300 mt-4">
                    +{getPendingReminders().length - 6} more reminders
                  </p>
                )}
              </div>
            </motion.div>
          )}

          {/* Groups Section */}
          <div className="lg:col-span-2">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" data-testid="groups-section-title">Your Groups</h2>
              <Button
                onClick={() => navigate('/create-group')}
                className="rounded-full gradient-primary hover:opacity-90"
                data-testid="create-group-btn"
              >
                <Plus className="w-5 h-5 mr-2" /> Create Group
              </Button>
            </div>

            {groups.length === 0 ? (
              <div className="bg-card rounded-2xl p-12 text-center border border-border">
                <div className="w-20 h-20 rounded-full gradient-primary flex items-center justify-center mx-auto mb-4">
                  <Target className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-2" data-testid="no-groups-title">No Groups Yet</h3>
                <p className="text-muted-foreground mb-6">Create your first group to start sharing expenses</p>
                <Button
                  onClick={() => navigate('/create-group')}
                  className="rounded-full gradient-primary"
                  data-testid="empty-state-create-btn"
                >
                  <Plus className="w-5 h-5 mr-2" /> Create Your First Group
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {groups.map((group, index) => {
                  const progress = (group.current_amount / group.goal_amount) * 100;
                  const groupBalance = balances?.by_group?.find(b => b.group_id === group.id);
                  return (
                    <motion.div
                      key={group.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.1 }}
                      onClick={() => navigate(`/group/${group.id}`)}
                      className="bg-card rounded-2xl p-6 border border-border hover:border-violet-300 dark:hover:border-violet-700 transition-all cursor-pointer card-hover"
                      data-testid={`group-card-${index}`}
                    >
                      <div className="flex justify-between items-start mb-4">
                        <div>
                          <h3 className="text-xl font-semibold mb-1">{group.name}</h3>
                          <p className="text-sm text-muted-foreground">{group.member_ids.length}/{group.num_members} members</p>
                        </div>
                        {groupBalance && (groupBalance.you_are_owed > 0 || groupBalance.you_owe > 0) && (
                          <div className="text-right">
                            {groupBalance.you_are_owed > 0 && (
                              <p className="text-sm text-green-600 dark:text-green-400 font-medium">
                                +₹{groupBalance.you_are_owed} owed to you
                              </p>
                            )}
                            {groupBalance.you_owe > 0 && (
                              <p className="text-sm text-red-600 dark:text-red-400 font-medium">
                                -₹{groupBalance.you_owe} you owe
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      <div className="mb-4">
                        <div className="flex justify-between text-sm mb-2">
                          <span className="text-muted-foreground">Progress</span>
                          <span className="font-semibold text-violet-600 dark:text-violet-400">{Math.min(progress, 100).toFixed(0)}%</span>
                        </div>
                        <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                          <div
                            className="h-full gradient-primary rounded-full transition-all duration-500"
                            style={{ width: `${Math.min(progress, 100)}%` }}
                          />
                        </div>
                      </div>

                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-xs text-muted-foreground">Current</p>
                          <p className="font-semibold">₹{group.current_amount.toLocaleString()}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground">Goal</p>
                          <p className="font-semibold">₹{group.goal_amount.toLocaleString()}</p>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Leaderboard */}
            <div className="bg-card rounded-2xl p-6 border border-border">
              <div className="flex items-center gap-2 mb-6">
                <Crown className="w-5 h-5 text-yellow-600" />
                <h2 className="text-lg font-semibold">Top Savers</h2>
              </div>
              <div className="space-y-3">
                {leaderboard.map((userData, index) => {
                  const lvl = getLevelBadge(userData.level || 1);
                  return (
                    <div
                      key={userData.id}
                      className="flex items-center gap-3 p-3 rounded-xl bg-muted/50"
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${
                        index === 0 ? 'bg-yellow-400 text-yellow-900' :
                        index === 1 ? 'bg-gray-300 text-gray-700' :
                        index === 2 ? 'bg-orange-400 text-orange-900' :
                        'bg-muted text-muted-foreground'
                      }`}>
                        {index + 1}
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-sm">{userData.name}</p>
                        <p className="text-xs text-muted-foreground">{userData.xp || 0} XP</p>
                      </div>
                      <span className={`level-badge ${lvl.class}`}>Lv{userData.level || 1}</span>
                      {userData.id === user.id && (
                        <span className="text-xs px-2 py-1 rounded-full bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 font-semibold">You</span>
                      )}
                    </div>
                  );
                })}
              </div>
              <Button
                onClick={() => navigate('/leaderboard')}
                variant="outline"
                className="w-full mt-4 rounded-full"
              >
                View Full Leaderboard
              </Button>
            </div>

            {/* Referral */}
            <div className="gradient-primary rounded-2xl p-6 text-white">
              <div className="flex items-center gap-2 mb-3">
                <Users className="w-5 h-5" />
                <h3 className="text-lg font-semibold">Invite Friends</h3>
              </div>
              <p className="text-sm opacity-90 mb-4">Share your code and earn 50 XP per referral!</p>
              <div className="bg-white/20 rounded-xl p-3 mb-3">
                <p className="text-xs opacity-75 mb-1">Your Code</p>
                <p className="font-mono font-bold text-lg">{user.referral_code}</p>
              </div>
              <Button
                onClick={() => {
                  navigator.clipboard.writeText(user.referral_code);
                  toast.success('Referral code copied!');
                }}
                variant="secondary"
                size="sm"
                className="w-full rounded-full bg-white/20 hover:bg-white/30 text-white border-0"
              >
                Copy Code
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Rating Modal */}
      <RatingModal 
        isOpen={showRatingModal} 
        onClose={() => setShowRatingModal(false)} 
        userId={user.id}
      />
    </div>
  );
};

export default Dashboard;
